import type { GuidebookHouseRules } from "./types"

interface Props {
  rules: GuidebookHouseRules
  /** Voice-aware section heading. Falls back to "Good to know". */
  sectionTitle?: string
}

interface RuleGroup {
  key: string
  icon: React.ReactNode
  title: string
  bullets: string[]
}

function showInGuidebook(rule: { enabled: boolean; surfaces?: { guidebook?: boolean } } | undefined): boolean {
  if (!rule?.enabled) return false
  return rule.surfaces?.guidebook !== false
}

export function HouseRulesSection({ rules, sectionTitle }: Props) {
  const groups: RuleGroup[] = []

  if (showInGuidebook(rules.quietHours)) {
    const qh = rules.quietHours!
    const bullets: string[] = []
    if (qh.startTime && qh.endTime) {
      bullets.push(`Quiet hours: ${qh.startTime} – ${qh.endTime}`)
    }
    if (qh.strictness === "strict") {
      bullets.push("Please keep noise to a minimum during these hours")
    }
    if (bullets.length) groups.push({ key: "quiet", icon: <MoonIcon />, title: "Quiet hours", bullets })
  }

  if (showInGuidebook(rules.smoking)) {
    const s = rules.smoking!
    const bullets: string[] = []
    bullets.push(friendlyValue(s.value))
    if (s.notes) bullets.push(s.notes)
    groups.push({ key: "smoking", icon: <NoSmokingIcon />, title: "Smoking", bullets })
  }

  if (showInGuidebook(rules.pets)) {
    const p = rules.pets!
    const bullets: string[] = []
    bullets.push(friendlyValue(p.value))
    if (p.notes) bullets.push(p.notes)
    groups.push({ key: "pets", icon: <PetIcon />, title: "Pets", bullets })
  }

  if (showInGuidebook(rules.visitors)) {
    const v = rules.visitors!
    const bullets: string[] = []
    bullets.push(friendlyValue(v.value))
    if (v.notes) bullets.push(v.notes)
    groups.push({ key: "visitors", icon: <VisitorIcon />, title: "Visitors", bullets })
  }

  if (showInGuidebook(rules.parties)) {
    const pt = rules.parties!
    const bullets: string[] = []
    bullets.push(friendlyValue(pt.value))
    if (pt.notes) bullets.push(pt.notes)
    groups.push({ key: "parties", icon: <PartyIcon />, title: "Parties & events", bullets })
  }

  if (rules.waste?.enabled) {
    const bullets: string[] = []
    if (rules.waste.binLocation) bullets.push(`Bins: ${rules.waste.binLocation}`)
    if (rules.waste.recyclingTypes?.length) bullets.push(`Recycle: ${rules.waste.recyclingTypes.join(", ")}`)
    if (rules.waste.notes) bullets.push(rules.waste.notes)
    if (bullets.length) groups.push({ key: "waste", icon: <RecycleIcon />, title: "Waste & recycling", bullets })
  }

  if (rules.safety?.enabled) {
    const bullets: string[] = []
    if (rules.safety.enabledRules?.length) {
      for (const r of rules.safety.enabledRules) {
        bullets.push(friendlySafety(r))
      }
    }
    if (rules.safety.customRules?.length) {
      for (const r of rules.safety.customRules) {
        bullets.push(r)
      }
    }
    if (bullets.length) groups.push({ key: "safety", icon: <SafetyIcon />, title: "Safety", bullets })
  }

  if (rules.parking?.instruction) {
    groups.push({ key: "parking", icon: <ParkingIcon />, title: "Parking", bullets: [rules.parking.instruction] })
  }

  if (rules.outdoor?.rules?.length) {
    const bullets = [...rules.outdoor.rules]
    if (rules.outdoor.notes) bullets.push(rules.outdoor.notes)
    groups.push({ key: "outdoor", icon: <OutdoorIcon />, title: "Outdoor areas", bullets })
  }

  if (rules.custom?.rules?.length) {
    groups.push({ key: "custom", icon: <NoteIcon />, title: "Additional notes", bullets: rules.custom.rules })
  }

  if (groups.length === 0) return null

  return (
    <div className="gb-section">
      <h2 className="gb-section__title">{sectionTitle || "Good to know"}</h2>
      <div className="gb-card">
        <div className="gb-rules__groups">
          {groups.map((g) => (
            <div className="gb-rules__group" key={g.key}>
              <span className="gb-rules__group-icon">{g.icon}</span>
              <div className="gb-rules__group-content">
                <div className="gb-rules__group-title">{g.title}</div>
                <ul className="gb-rules__group-bullets">
                  {g.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function friendlyValue(val?: string): string {
  switch (val) {
    case "not_allowed": return "Not permitted"
    case "allowed": return "Allowed"
    case "allowed_outside": return "Allowed outside only"
    case "approval_required": return "Please ask your host first"
    case "designated_areas": return "Designated areas only"
    default: return val || "Please check with your host"
  }
}

function friendlySafety(rule: string): string {
  switch (rule) {
    case "fire_extinguisher": return "Fire extinguisher available"
    case "first_aid_kit": return "First aid kit available"
    case "smoke_detector": return "Smoke detector installed"
    case "carbon_monoxide_detector": return "Carbon monoxide detector installed"
    case "security_camera_exterior": return "Security camera on exterior"
    default: return rule.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }
}

function MoonIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M13.5 10.5A6 6 0 0 1 7.5 4.5a6 6 0 1 0 6 6Z" /></svg>
}
function NoSmokingIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="9" cy="9" r="7" /><path d="M4 14L14 4" /></svg>
}
function PetIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="6" cy="5" r="1.5" /><circle cx="12" cy="5" r="1.5" /><circle cx="4" cy="9" r="1.5" /><circle cx="14" cy="9" r="1.5" /><path d="M6.5 13.5C7 11 8 10 9 10s2 1 2.5 3.5c.3 1.5-1 2-2.5 2s-2.8-.5-2.5-2Z" /></svg>
}
function VisitorIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="9" cy="5" r="3" /><path d="M3 15c0-3.3 2.7-6 6-6s6 2.7 6 6" /></svg>
}
function PartyIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M9 2l1.5 4H15l-3.5 2.5 1 4.5L9 10.5 5.5 13l1-4.5L3 6h4.5L9 2z" /></svg>
}
function RecycleIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M9 2l3 5H6l3-5zM3 11l3-5 3 5H3zM12 11l3-5v5h-3z" /></svg>
}
function SafetyIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M9 2L3 5v4c0 4 2.5 6 6 7 3.5-1 6-3 6-7V5L9 2z" /></svg>
}
function ParkingIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="12" height="12" rx="2" /><path d="M7 13V5h3a3 3 0 0 1 0 6H7" /></svg>
}
function OutdoorIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M9 2l5 8H4l5-8z" /><path d="M6 10l-3 6h12l-3-6" /><path d="M9 2v4" /></svg>
}
function NoteIcon() {
  return <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 3h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" /><path d="M6 7h6M6 10h6M6 13h3" /></svg>
}
